// TODO: CU-86dvx98k1 Convert CSelect and CMenu to use Kibble
import {
  Box,
  CMenu,
  CMenuButton,
  CMenuList,
  CSelect,
  HStack,
  Portal,
  Square,
  useDisclosure,
} from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import useCheckChannelExportability from 'pages/Conversations/hooks/useCheckChannelExportability';
import React, { useCallback, useState } from 'react';
import { Placement } from 'shared/enums/Placement';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ChannelStatus,
  ChannelStatusAction,
  ChannelStatusFragment,
  MessageType,
  useCreateSystemChannelMessageMutation,
  useUpdateChannelStatusMutation,
} from 'shared/types/graphql';
import { colorContrast } from 'shared/utils';
import ChannelStatusMenuList from './ChannelStatusMenuList';
import StatusChangeModal from './StatusChangeModal';

interface IChannelStatusSelectProps {
  currentStatus?: Pick<ChannelStatus, 'id' | 'name' | 'color'>;
  channelId?: string;
  placement: Placement;
  loading?: boolean;
  style?: 'dropdown' | 'badge';
  onStatusSelect?: (channelStatusId: string) => Promise<void>;
  isDisabled?: boolean;
  maxW?: string;
  showRightIcon?: boolean;
}

/**
 * A shared status selector
 *
 * @param {Pick<ChannelStatus, 'id' | 'name' | 'color'>} currentStatus The currently selected status
 * @param {string} channelId A channel ID used for checking to see if
 * we can update the channel, and to update the channel (optional)
 * @param {Placement} placement Placement of the status menu list popover
 * @param {boolean} loading Loading state to disable the component (optional)
 * @param {'dropdown' | 'badge'} style Whether to style the component as a badge (default) or dropdown
 * @param {Promise<void>} onStatusSelect Action to perform on selecting a status (optional)
 * @param {boolean} showRightIcon Ability to toggle visibility of right caret icon
 */
const ChannelStatusSelect = ({
  currentStatus,
  channelId,
  placement,
  loading = false,
  style = 'badge',
  onStatusSelect,
  isDisabled = false,
  maxW = 'unset',
  showRightIcon = true,
}: IChannelStatusSelectProps): JSX.Element => {
  const { currentClinicId, clinicUser } = useClinicUser();
  const [canReopenChannel, setCanReopenChannel] = useState(true);
  const [selectedChannelStatus, setSelectedChannelStatus] = useState<ChannelStatusFragment>();

  const {
    isOpen: isStatusChangeModalOpen,
    onOpen: onStatusChangeModalOpen,
    onClose: onStatusChangeModalClose,
  } = useDisclosure();

  const [updateChannel, { loading: isUpdateChannelLoading }] = useUpdateChannelStatusMutation();

  const [createSystemMessage] = useCreateSystemChannelMessageMutation();

  const handleUpdateChannel = useCallback(
    async (newChannelStatus: ChannelStatusFragment): Promise<void> => {
      if (
        isUpdateChannelLoading ||
        !channelId ||
        (newChannelStatus.channelStatusAction === ChannelStatusAction.Active && !canReopenChannel)
      ) {
        return;
      }
      const { id, name, color, channelStatusAction } = newChannelStatus;
      await updateChannel({
        variables: {
          where: { id: channelId },
          data: { channelStatus: { connect: { id } } },
        },
        optimisticResponse: {
          updateOneChannel: {
            __typename: 'Channel',
            clinicId: currentClinicId,
            id: channelId,
            channelStatusId: id,
            channelStatus: {
              __typename: 'ChannelStatus',
              id,
              name,
              color,
              channelStatusAction,
            },
          },
        },
      });
      // Add system message to track archived channels
      if (
        channelStatusAction === ChannelStatusAction.Inactive ||
        channelStatusAction === ChannelStatusAction.InactivePermanently
      ) {
        await createSystemMessage({
          variables: {
            data: {
              attributes: {
                overrideShouldCountTowardsUnreadMessageCount: false,
              },
              messageType: MessageType.Note,
              body: `Conversation was ${channelStatusAction === ChannelStatusAction.Inactive ? 'closed' : 'archived'
                } by ${clinicUser?.displayName || `${clinicUser?.firstName} ${clinicUser?.lastName}`}`,
              channelId,
            },
          },
        });
      }
    },
    [
      isUpdateChannelLoading,
      channelId,
      canReopenChannel,
      updateChannel,
      currentClinicId,
      createSystemMessage,
      clinicUser?.displayName,
      clinicUser?.firstName,
      clinicUser?.lastName,
    ],
  );

  const onChangeStatus = useCallback(
    (channelStatusObj?: ChannelStatusFragment): void => {
      const channelStatus = channelStatusObj || selectedChannelStatus;
      channelId && channelStatus && handleUpdateChannel(channelStatus);
      onStatusSelect && channelStatus && onStatusSelect(channelStatus.id);
    },
    [channelId, selectedChannelStatus, handleUpdateChannel, onStatusSelect],
  );

  const { checkChannelExportability } = useCheckChannelExportability();

  const handleChannelStatusSelect = useCallback(
    async (channelStatus: ChannelStatusFragment): Promise<void> => {
      if (!channelStatus.isExportedToPims) {
        onChangeStatus(channelStatus);
        return;
      }

      const isExportable = channelId ? !(await checkChannelExportability(channelId)) : false;
      if (!isExportable) {
        onStatusChangeModalOpen();
        setSelectedChannelStatus(channelStatus);
      } else {
        onChangeStatus(channelStatus);
      }
    },
    [checkChannelExportability, channelId, onChangeStatus, onStatusChangeModalOpen],
  );

  // TODO: CU-86dvx98k1 This needs to be converted to use Kibble Menu and Kibble Select components
  return (
    <Box>
      <CMenu isLazy={true} placement={placement || Placement.BottomEnd} strategy="fixed" autoSelect={false}>
        {({ onClose }): JSX.Element => (
          <>
            {style === 'badge' ? (
              <CMenuButton
                data-testid="channel-status-select-button"
                as={Button}
                size="xs"
                onClick={(event: React.MouseEvent): void => event.stopPropagation()}
                isDisabled={isDisabled || loading}
                maxW={maxW}
                {...(currentStatus?.color && {
                  bg: currentStatus.color,
                  color: colorContrast(currentStatus.color),
                  _hover: { bg: `${currentStatus.color}CC` },
                  _active: { bg: currentStatus.color },
                })}
                {...(showRightIcon && { iconName: 'chevronDown', iconPosition: 'right' })}
              >
                <Text
                  as="p"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  maxW="100px"
                  color="text.onContrast"
                  data-testid={'channel-status-select-button-text'}
                  size="xs"
                >
                  {currentStatus?.name || ''}
                </Text>
              </CMenuButton>
            ) : (
                <CSelect
                  data-testid="channel-status-select-button"
                  as={CMenuButton}
                  w="220px"
                  h="36px"
                  sx={{ paddingLeft: '12px' }}
                  fontSize="sm"
                  _focus={{ borderColor: 'border.default' }}
                  backgroundColor="background.default"
                  onClick={(event: React.MouseEvent): void => event.stopPropagation()}
                  disabled={isDisabled || loading}
                >
                  <HStack spacing={3}>
                    <Square bgColor={currentStatus?.color || ''} size="1em" borderRadius={4} />
                    <Text isTruncated>{currentStatus?.name || ''}</Text>
                  </HStack>
                </CSelect>
              )}

            <Portal>
              <CMenuList
                zIndex="popover"
                maxH="400px"
                maxW="300px"
                overflowY="auto"
                overflowX="hidden"
                fontSize="sm"
                transition="none"
              >
                <ChannelStatusMenuList
                  channelId={channelId}
                  onMenuClose={onClose}
                  handleChannelStatusSelect={handleChannelStatusSelect}
                  onSetCanReopenChannel={setCanReopenChannel}
                  canReopenChannel={canReopenChannel}
                />
              </CMenuList>
            </Portal>
          </>
        )}
      </CMenu>
      <StatusChangeModal
        isOpen={isStatusChangeModalOpen}
        onClose={onStatusChangeModalClose}
        statusName={selectedChannelStatus?.name || ''}
        onChangeStatus={onChangeStatus}
      />
    </Box>
  );
};

export default ChannelStatusSelect;
